import {
  getRecordConsolePlugin,
  type LogData,
  type LogLevel,
} from '@rrweb/rrweb-plugin-console-record'

/**
 * Niveaux enregistrés : les messages utiles au débogage. `error` comprend les exceptions non
 * gérées et les promesses rejetées sans `catch` (le plugin écoute `error` et
 * `unhandledrejection`). Les méthodes de mise en forme (`group`, `table`, `time`…) sont laissées
 * de côté.
 */
const CONSOLE_LEVELS: LogLevel[] = ['log', 'info', 'debug', 'warn', 'error', 'assert', 'trace']

/** Argument masqué (`maskConsole`), sérialisé comme le plugin sérialise les arguments. */
const MASKED_ARGUMENT = JSON.stringify('***')

/** Taille maximale d'un argument sérialisé, comme les corps réseau (cf. PRD). */
const MAX_ARGUMENT_LENGTH = 10 * 1024
const TRUNCATED = '… [tronqué]'

/**
 * Argument trop long (un objet énorme, un texte de plusieurs Mo) : tronqué, sous la forme d'un
 * texte (le JSON d'un objet coupé en deux ne serait plus lisible).
 */
function truncate(argument: string): string {
  if (argument.length <= MAX_ARGUMENT_LENGTH) return argument
  let text = argument
  try {
    const value: unknown = JSON.parse(argument)
    if (typeof value === 'string') text = value
  } catch {
    // Argument non JSON (ne devrait pas arriver) : tronqué tel quel.
  }
  let kept = MAX_ARGUMENT_LENGTH - TRUNCATED.length - 2
  let truncated = JSON.stringify(text.slice(0, kept) + TRUNCATED)
  // Les caractères échappés (guillemets, retours à la ligne) allongent le texte sérialisé.
  while (truncated.length > MAX_ARGUMENT_LENGTH) {
    kept -= truncated.length - MAX_ARGUMENT_LENGTH
    truncated = JSON.stringify(text.slice(0, kept) + TRUNCATED)
  }
  return truncated
}

export type ConsoleOptions = {
  /** Remplace chaque argument (et le message des exceptions) par `***`. */
  mask: boolean
}

/**
 * Plugin console officiel de rrweb (`rrweb/console@1`, format attendu par l'ingestion et le
 * lecteur), dont chaque message passe par belvdr avant d'être enregistré, dans le navigateur :
 * arguments masqués ou tronqués.
 */
export function consolePlugin({ mask }: ConsoleOptions): ReturnType<typeof getRecordConsolePlugin> {
  const plugin = getRecordConsolePlugin({ level: CONSOLE_LEVELS })
  const transform = (data: LogData): LogData => ({
    ...data,
    payload: data.payload.map((argument) => (mask ? MASKED_ARGUMENT : truncate(argument))),
  })
  const observe = plugin.observer
  if (!observe) return plugin
  const observer: typeof observe = (cb, win, options) =>
    observe((data) => cb(transform(data as LogData)), win, options)
  return { ...plugin, observer }
}
